import { useEffect, useState, useSyncExternalStore } from "react";
import { chatOutboxRevision, subscribeChatOutbox, selectionView, observeAcceptedSelections, chooseLocalSelection, reviewSelectionDraft,
  type ChatComponent, type SelectionChoice } from "@rekonas/data-access";

/** Local intent is independent of authoritative snapshots and frozen admissions. */
export function useComponentSelection<T>({ component, recording, value, choice, selectedValue, onDraftError }: {
  component: ChatComponent; recording: string; value: (component: ChatComponent) => T;
  choice: (value: T) => SelectionChoice; selectedValue: (choice: SelectionChoice) => T;
  onDraftError?: (message: string) => void;
}) {
  useSyncExternalStore(subscribeChatOutbox, chatOutboxRevision);
  const [failure, setFailure] = useState<{ value: T; message: string } | null>(null);
  useEffect(() => {
    try { observeAcceptedSelections(recording, [component]); }
    catch { /* Read/write failures are exposed below; they never start a send. */ }
  }, [recording, component]);
  let current;
  try { current = selectionView(recording, component); } catch { /* Saved card remains readable when local persistence is unavailable. */ }
  const error = failure?.message || (!current ? "Local selection storage is unavailable. This choice cannot be sent yet." : "");
  function report(message: string) { onDraftError?.(message); }
  function choose(next: T) {
    try { chooseLocalSelection(recording, component, choice(next)); setFailure(null); report(""); }
    catch { const message = "Selection is local only: draft storage is unavailable or full. Restore storage or discard this draft before sending."; setFailure({ value: next, message }); report(message); }
  }
  function review(keep: boolean) {
    try { reviewSelectionDraft(recording, component, keep); setFailure(null); report(""); }
    catch { report("Draft storage is unavailable. Sending remains disabled."); }
  }
  return { selected: failure ? failure.value : current ? selectedValue(current.choice) : value(component),
    accepted: current ? selectedValue(current.accepted) : value(component),
    dirty: Boolean(failure || current?.dirty), conflict: Boolean(current?.conflict),
    revision: current?.revision ?? component.state.revision, error, choose,
    keepDraft: () => review(true), discard: () => review(false) };
}
